/**
 * DealerConnect Dynamic Script Loader
 *
 * Provides a way to dynamically load the DealerConnect package from either
 * a local build or a CDN URL. Similar to how V6 config is loaded in the
 * main application.
 *
 * This loader:
 * - Dynamically injects the script tag
 * - Returns a typed reference to the loaded DealerConnect API
 * - Supports switching between local and server sources easily
 * - Ensures globalThis.dealerConnect is available before continuing
 */

// Import the interface types for typing - these are compile-time only
import type { DealerConnectEntryPoint } from './package/dealerconnect-entrypoint-intf';

/**
 * Source configuration for loading DealerConnect
 */
export interface DealerConnectSourceConfig {
  /** Use local build from relative path */
  local?: boolean;
  /** Local path (relative to document root) */
  localPath?: string;
  /** CDN base URL (version will be appended) */
  cdnBase?: string;
  /** Version string for CDN URL */
  version?: string;
}

/**
 * Default configuration values
 */
const defaultConfig: Required<DealerConnectSourceConfig> = {
  local: false,
  localPath: 'dealerconnect/dealerconnect-entrypoint.js',
  cdnBase: 'https://webmodule-softtech.azureedge.net/dealer-connect',
  version: 'v2026SP2.0.0'
};

/**
 * Reason codes for loader failures
 */
export enum DealerConnectLoaderFailureReason {
  /** No failure - load was successful */
  None = 'none',
  /** Script failed to load */
  ScriptLoadFailed = 'script-load-failed',
  /** DealerConnect did not initialize on globalThis */
  GlobalNotAvailable = 'global-not-available',
  /** Already loading - concurrent load attempt */
  AlreadyLoading = 'already-loading'
}

/**
 * Result of the DealerConnect loader
 */
export interface DealerConnectLoaderResult {
  /** Whether the load was successful */
  success: boolean;
  /** The API instance if successful */
  api?: DealerConnectEntryPoint;
  /** The source URL that was loaded */
  source?: string;
  /** Reason for failure if success is false */
  failureReason?: DealerConnectLoaderFailureReason;
  /** Error message if failure */
  message?: string;
  /** The underlying error if one occurred */
  error?: Error;
}

// Module state
let scriptElement: HTMLScriptElement | null = null;
let loadedApi: DealerConnectEntryPoint | null = null;
let isLoading = false;
let loadPromise: Promise<DealerConnectLoaderResult> | null = null;

/**
 * Declare the global type extension
 */
declare global {
  // eslint-disable-next-line no-var
  var dealerConnect: DealerConnectEntryPoint | undefined;
}

/**
 * Get the compiled source URL based on configuration
 */
function getSourceUrl(config: DealerConnectSourceConfig): string {
  const mergedConfig = { ...defaultConfig, ...config };

  if (mergedConfig.local) {
    return mergedConfig.localPath;
  }

  return `${mergedConfig.cdnBase}/${mergedConfig.version}/dealerconnect-entrypoint.js`;
}

/**
 * Load DealerConnect dynamically
 *
 * @param config - Source configuration (optional, defaults to local build)
 * @returns Promise resolving to the load result with API reference
 *
 * @example
 * // Load from local build (default)
 * const result = await loadDealerConnect();
 * if (result.success) {
 *   const api = result.api;
 *   // Use the API...
 * }
 *
 * @example
 * // Load from CDN
 * const result = await loadDealerConnect({
 *   local: false,
 *   version: 'v20-26SP2.0.0'
 * });
 */
export async function loadDealerConnect(config: DealerConnectSourceConfig = {}): Promise<DealerConnectLoaderResult> {
  // If already loaded successfully, return the cached API
  if (loadedApi && globalThis.dealerConnect) {
    return { success: true, api: loadedApi };
  }

  // If currently loading, return the existing promise
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Start new load
  isLoading = true;
  loadPromise = loadDealerConnectInternal(config);

  try {
    return await loadPromise;
  } finally {
    isLoading = false;
    loadPromise = null;
  }
}

/**
 * Internal loader implementation
 */
async function loadDealerConnectInternal(config: DealerConnectSourceConfig): Promise<DealerConnectLoaderResult> {
  const source = getSourceUrl(config);

  // Clean up any existing script element if reloading
  if (scriptElement) {
    scriptElement.remove();
    scriptElement = null;
    loadedApi = null;
  }

  // Create and configure script element
  const script = document.createElement('script');
  scriptElement = script;

  try {
    await new Promise<void>((resolve, reject) => {
      script.onload = () => {
        // Verify globalThis.dealerConnect is available
        if (!globalThis.dealerConnect) {
          reject(new Error('DealerConnect script loaded but globalThis.dealerConnect is not available'));
          return;
        }
        resolve();
      };

      script.onerror = event => {
        console.error(`Failed to load DealerConnect from ${source}:`, event);

        // Try to fetch directly to get more error info
        fetch(source)
          .then(response => {
            if (!response.ok) {
              console.error(`Network response was not ok: ${response.status} ${response.statusText}`);
            } else {
              console.log('Script can be fetched directly, but failed to load via script tag');
            }
          })
          .catch(err => {
            console.error('Fetch attempt also failed:', err);
          });

        reject(new Error(`Failed to load DealerConnect from ${source}`));
      };

      script.src = source;
      document.head.appendChild(script);
    });

    // Success - cache and return the API
    loadedApi = globalThis.dealerConnect!;

    return {
      success: true,
      api: loadedApi,
      source
    };
  } catch (error) {
    console.error('DealerConnect load failed:', error);

    // Clean up on failure
    scriptElement?.remove();
    scriptElement = null;
    loadedApi = null;

    const err = error instanceof Error ? error : new Error(String(error));
    const isGlobalMissing = err.message.includes('globalThis');

    return {
      success: false,
      source,
      failureReason: isGlobalMissing
        ? DealerConnectLoaderFailureReason.GlobalNotAvailable
        : DealerConnectLoaderFailureReason.ScriptLoadFailed,
      message: err.message,
      error: err
    };
  }
}

/**
 * Get the currently loaded DealerConnect API (if any)
 * Returns undefined if not yet loaded
 */
export function getDealerConnectApi(): DealerConnectEntryPoint | undefined {
  return loadedApi ?? undefined;
}

/**
 * Check if DealerConnect is loaded and available
 */
export function isDealerConnectLoaded(): boolean {
  return loadedApi !== null && globalThis.dealerConnect !== undefined;
}

/**
 * Unload DealerConnect (for testing or hot-reload scenarios)
 * Removes the script and clears cached state
 */
export function unloadDealerConnect(): void {
  if (scriptElement) {
    scriptElement.remove();
    scriptElement = null;
  }
  loadedApi = null;
  globalThis.dealerConnect = undefined;
}
